import {Link45deg} from "react-bootstrap-icons";
import c from './performedTests.module.css';

/**
 * A run's status as written by the API: pending and in_process while it is
 * queued or running, completed or failed once it is done. The portal
 * synthesises 'success' for runs that already have results.
 *
 * It used to render as the raw lowercase string, which read as API output and
 * gave the one column that says "something went wrong" no visual weight at all.
 */
const STATUS = {
    success:    {label: 'Success',    tone: c.statusOk},
    completed:  {label: 'Success',    tone: c.statusOk},
    pending:    {label: 'Pending',    tone: c.statusBusy},
    in_process: {label: 'In process', tone: c.statusBusy},
    failed:     {label: 'Failed',     tone: c.statusFailed},
    error:      {label: 'Failed',     tone: c.statusFailed},
};

const statusBadge = (cell) => {
    const key = String(cell ?? '').toLowerCase();
    // An unrecognised status still shows - as itself, in neutral - rather than
    // vanishing behind a fallback label.
    const {label, tone} = STATUS[key] || {label: cell || 'Unknown', tone: c.statusNeutral};
    return <span className={`${c.statusBadge} ${tone}`}>{label}</span>;
};

/**
 * "11 of 21" - predicted out of submitted.
 *
 * The column showed the predicted count alone, which read as the size of the
 * whole run. A request of 21 rows containing 10 repeated structures rendered as
 * "11" with nothing saying where the other 10 went - and for a multi-block
 * endpoint the report then carried three pages per compound, so the number on
 * screen matched neither what was submitted nor what the PDF contained. It
 * looked like a bug and was read as one.
 *
 * `not_predicted` has always come back beside it; only the total was missing.
 *
 * While a run is still going the API sends predicted: null with not_predicted
 * set to the whole request, so there is no count yet - an em dash rather than
 * "0 of 21", which would claim that nothing scored.
 */
const predictedOfSubmitted = (cell, row) => {
    const done = typeof cell === 'number' ? cell : null;
    const skipped = typeof row?.not_predicted === 'number' ? row.not_predicted : 0;
    const submitted = (done ?? 0) + skipped;

    if (!submitted) return done ?? '\u2014';

    const title = done === null
        ? `${submitted} compound(s) submitted`
        : `${done} of ${submitted} compound(s) predicted; ${skipped} repeated or invalid`;

    return <span title={title}>{done ?? '\u2014'} of {submitted}</span>;
};

export const columnsTable = (openLabelingModal) => {
    return [
        {
            dataField: 'date',
            text: 'Date',
            // tabular figures: without them 1 is narrower than 0 and no column
            // of numbers lines up down the page
            classes: () => c.numericCell,
            headerStyle: (colum, colIndex) => {
                return {textAlign: 'center', width: '180px', verticalAlign:'middle',padding:'0'};
            }
        }, {
            dataField: 'test_name',
            text: 'Test Name',
            classes: () => 'overFlowHiddenRow',
            headerStyle: (colum, colIndex) => {
                return {textAlign: 'center', overflow:'hidden', verticalAlign:'middle',padding:'0'};
            }
        }, {
            dataField: 'predicted',
            text: 'Predicted Compounds',
            classes: () => `reportFileCenter ${c.numericCell}`,
            headerStyle: (colum, colIndex) => {
                return {width: '150px', textAlign: 'center', verticalAlign:'middle',padding:'0'};
            },
            formatter: predictedOfSubmitted,
        }, {
            dataField: 'test_status',
            text: 'Status',
            classes: () => 'reportFileCenter',
            headerStyle: (colum, colIndex) => {
                return {width: '120px', textAlign: 'center', verticalAlign:'middle',padding:'0'};
            },
            formatter: (cell) => statusBadge(cell),
        }, {
            dataField: 'report_file',
            text: 'Report File',
            classes: () => 'migrationLogsClass reportFileCenter',
            headerStyle: (colum, colIndex) => {
                return {width: '150px', textAlign: 'center', verticalAlign:'middle',padding:'0'};
            },
            formatter: (cell, row) => {
                return <a style={{fontSize: '16px', whiteSpace: 'nowrap'}}
                          aria-hidden="true"
                          rel="noreferrer"
                          target='_blank'
                          title={cell}
                          href={cell}><Link45deg size={20}/> View </a>
            },
        },
    ];
}
